import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, ShieldCheck, Send, Download, Building2, Store,
  PenLine, Bell, MapPin, Landmark, Clock, CheckCircle2,
  X, Check, ArrowRight, LogIn, Search, AlertCircle, Info,
} from 'lucide-react';

/* ── Vérification SIREN via l'API gouvernementale française ── */
async function lookupSiren(siren) {
  const r = await fetch(
    `https://recherche-entreprises.api.gouv.fr/search?q=${siren}&page=1&per_page=1`
  );
  if (!r.ok) throw new Error('Service indisponible');
  const data = await r.json();
  if (!data.results || data.results.length === 0) return null;
  const e = data.results[0];
  return {
    nom: e.nom_complet || e.nom_raison_sociale,
    siege: e.siege,
    siren: e.siren,
  };
}

/* ── Jours restants avant le 1er septembre 2026 ── */
function joursRestants() {
  const deadline = new Date('2026-09-01');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((deadline - today) / (1000 * 60 * 60 * 24)));
}

/* ── Mockup facture réaliste ── */
function InvoiceMockup() {
  return (
    <div style={{ position: 'relative', maxWidth: 460, width: '100%' }}>
      {/* Badge Factur-X flottant */}
      <div className="anim-float" style={{
        position: 'absolute', top: -26, right: 8, zIndex: 2,
        background: '#fff', border: '1px solid #e2e8f0',
        borderRadius: 12, padding: '12px 15px',
        boxShadow: '0 12px 32px rgba(15,23,42,.14)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        </span>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Format</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Factur-X conforme</div>
        </div>
      </div>

      {/* Carte facture */}
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 24px 60px rgba(15,23,42,.16)',
        marginTop: 20,
      }}>
        {/* Header */}
        <div style={{ padding: '20px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Facture</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 2, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>F-2026-0142</div>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', borderRadius: 9999, fontSize: 12, fontWeight: 600, color: '#059669', background: '#ecfdf5', border: '1px solid #a7f3d0' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
            Acceptée
          </span>
        </div>

        {/* Transmission */}
        <div style={{ padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 9, background: '#f8fafc', borderBottom: '1px solid #f1f5f9', fontSize: 12.5, color: '#475569' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          Transmise via <strong style={{ color: '#0f172a' }}>plateforme agréée · Super PDP</strong>
        </div>

        {/* Lignes */}
        <div style={{ padding: '18px 22px' }}>
          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Facturé à</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginTop: 3, marginBottom: 16 }}>SARL Transports Marchand</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {[
              { label: 'Remplacement pare-chocs AV', amount: '420,00 €' },
              { label: 'Peinture cabine + raccord',   amount: '510,00 €' },
              { label: "Main d'œuvre · 4 h",          amount: '280,00 €' },
            ].map((l) => (
              <div key={l.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
                <span style={{ color: '#475569' }}>{l.label}</span>
                <span style={{ color: '#0f172a', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{l.amount}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Total TTC</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>1 452,00 €</span>
          </div>
        </div>

        {/* Étapes transmission */}
        <div style={{ padding: '14px 22px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center' }}>
          {['Émise', 'Déposée', 'Acceptée'].map((step, i) => (
            <React.Fragment key={step}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#059669', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                </div>
                <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>{step}</span>
              </div>
              {i < 2 && <div style={{ flex: 1, height: 2, background: '#059669', margin: '0 4px', marginBottom: 14 }} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const contactRef    = useRef(null);
  const annuaireRef   = useRef(null);
  const reformeRef    = useRef(null);
  const solutionRef   = useRef(null);
  const faqRef        = useRef(null);
  const [jours] = useState(joursRestants);

  const [form, setForm] = useState({ nom: '', email: '', telephone: '', siren: '', message: '' });
  const [sirenInfo, setSirenInfo] = useState(null);
  const [sirenStatus, setSirenStatus] = useState('idle');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');
  const sirenGenRef = useRef(0);
  const [openFaq, setOpenFaq] = useState(null);

  // Vérification annuaire e-invoicing
  const [annuaireSiren, setAnnuaireSiren] = useState('');
  const [annuaireResult, setAnnuaireResult] = useState(null);
  const [annuaireLoading, setAnnuaireLoading] = useState(false);
  const [annuaireError, setAnnuaireError] = useState('');

  const checkAnnuaire = async (e) => {
    e.preventDefault();
    const clean = annuaireSiren.replace(/\D/g, '').slice(0, 9);
    if (clean.length !== 9) return;
    setAnnuaireLoading(true);
    setAnnuaireResult(null);
    setAnnuaireError('');
    try {
      const r = await fetch(`/api/public/annuaire/${clean}`);
      if (!r.ok) throw new Error();
      const data = await r.json();
      setAnnuaireResult(data);
    } catch {
      setAnnuaireError('Erreur lors de la vérification. Réessayez.');
    } finally {
      setAnnuaireLoading(false);
    }
  };

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const scrollToContact  = () => scrollTo(contactRef);
  const scrollToAnnuaire = () => scrollTo(annuaireRef);

  const handleSirenChange = async (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 9);
    setForm(f => ({ ...f, siren: clean }));
    setSirenInfo(null);
    if (clean.length === 9) {
      const gen = ++sirenGenRef.current;
      setSirenStatus('loading');
      try {
        const info = await lookupSiren(clean);
        if (sirenGenRef.current !== gen) return;
        if (info) { setSirenInfo(info); setSirenStatus('found'); }
        else setSirenStatus('notfound');
      } catch {
        if (sirenGenRef.current !== gen) return;
        setSirenStatus('error');
      }
    } else {
      setSirenStatus('idle');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSendError('');
    try {
      const r = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, raison_sociale: sirenInfo?.nom || null }),
      });
      if (!r.ok) throw new Error();
      setSent(true);
    } catch {
      setSendError('Une erreur est survenue. Réessayez ou écrivez-nous directement.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text)', background: 'var(--color-surface)' }}>

      {/* ── BANDEAU URGENCE ── */}
      <div style={{
        background: '#0f172a',
        color: '#fff',
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        fontSize: 14,
      }}>
        <span className="anim-pulse" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-warning)', flexShrink: 0, display: 'inline-block' }} />
        <span style={{ color: 'var(--color-border)' }}>
          Réforme en vigueur le <strong style={{ color: 'var(--color-surface)' }}>1er septembre 2026</strong> — plus que
        </span>
        <span style={{
          background: 'var(--color-primary)', color: 'var(--color-surface)',
          borderRadius: 6, padding: '2px 10px',
          fontWeight: 700, fontSize: 14,
        }}>
          {jours} jours
        </span>
        <button
          onClick={scrollToContact}
          style={{
            background: 'none', border: 'none', color: 'var(--color-info-border)',
            cursor: 'pointer', fontSize: 14, fontFamily: 'var(--font-sans)',
            textDecoration: 'underline', padding: 0,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          Prendre contact <ArrowRight size={14} />
        </button>
      </div>

      {/* ── NAVBAR ── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-sm)',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/e-facture.png" alt="Facto" style={{ height: 34, width: 'auto' }} />
            <span style={{ fontWeight: 800, fontSize: 20 }}>
              <span style={{ color: 'var(--color-text)' }}>E-</span><span style={{ color: 'var(--color-primary)' }}>facture</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {[
              { label: 'Ce qui change', ref: reformeRef },
              { label: 'La solution',   ref: solutionRef },
              { label: 'FAQ',           ref: faqRef },
            ].map(({ label, ref }) => (
              <button key={label} onClick={() => scrollTo(ref)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-text-secondary)', fontFamily: 'var(--font-sans)',
                fontSize: 14, padding: '8px 12px', borderRadius: 'var(--radius)',
              }}>
                {label}
              </button>
            ))}
            <button
              onClick={scrollToAnnuaire}
              style={{
                marginLeft: 8,
                background: 'none',
                border: '1.5px solid var(--color-border)',
                color: 'var(--color-text-secondary)',
                borderRadius: 'var(--radius)',
                padding: '7px 16px',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Vérifier ma conformité
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/login')}
              style={{ marginLeft: 4, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <LogIn size={14} />
              Accès client
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section style={{
        background: 'linear-gradient(160deg, var(--color-bg) 0%, var(--color-primary-soft) 100%)',
        padding: '80px 24px 96px',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
          <div>
            {/* Badge */}
            <div className="anim-rise" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'var(--color-primary-soft)', border: '1px solid var(--color-info-border)',
              borderRadius: 'var(--radius-full)', padding: '6px 14px', marginBottom: 28,
              fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary)',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4"/><path d="m16.2 7.8 2.9-2.9"/><path d="M18 12h4"/><circle cx="12" cy="14" r="8"/><path d="M12 14v-3"/></svg>
              Réforme 2026 · Facturation électronique
            </div>

            <h1 className="anim-rise-d1" style={{ fontSize: 54, fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 22 }}>
              <span style={{ color: 'var(--color-text)' }}>La facture électronique<br />devient obligatoire.</span>
              <br />
              <span style={{ color: 'var(--color-primary)' }}>E-facture s'en charge.</span>
            </h1>

            <p className="anim-rise-d2" style={{ fontSize: 18, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: 30, maxWidth: 520 }}>
              Dès le 1<sup>er</sup> septembre 2026, toutes les entreprises devront émettre leurs factures B2B au format électronique. E-facture transmet automatiquement via <strong style={{ color: 'var(--color-text)' }}>Super PDP, plateforme agréée DGFIP</strong> : vos factures sont conformes et suivies — sans rien changer à votre quotidien.
            </p>

            <div className="anim-rise-d3" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 30 }}>
              <button
                onClick={scrollToAnnuaire}
                className="btn btn-primary"
                style={{ fontSize: 16, fontWeight: 600, padding: '14px 24px', borderRadius: 9, boxShadow: '0 6px 16px rgba(37,99,235,.28)', display: 'inline-flex', alignItems: 'center', gap: 9 }}
              >
                Vérifier ma conformité
                <ArrowRight size={17} />
              </button>
              <button
                onClick={() => scrollTo(reformeRef)}
                className="btn btn-secondary"
                style={{ fontSize: 16, fontWeight: 600, padding: '14px 22px', borderRadius: 9 }}
              >
                Voir le calendrier
              </button>
            </div>

            <div className="anim-rise-d4" style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', color: 'var(--color-neutral-badge)', fontSize: 13, fontWeight: 500 }}>
              {[
                'Transmission via Super PDP, agréé DGFIP',
                'Factur-X · UBL · CII',
                'Données hébergées en France',
              ].map(item => (
                <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="anim-rise-late" style={{ display: 'flex', justifyContent: 'center' }}>
            <InvoiceMockup />
          </div>
        </div>
      </section>

      {/* ── ANNUAIRE CHECK ── */}
      <section ref={annuaireRef} style={{ padding: '56px 24px', background: 'var(--color-bg)', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, marginBottom: 10 }}>
              Votre entreprise est-elle inscrite à l'annuaire officiel ?
            </h2>
            <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', maxWidth: 540, margin: '0 auto' }}>
              L'annuaire DGFIP recense les entreprises enregistrées pour la facturation électronique. Vérifiez votre statut en quelques secondes.
            </p>
          </div>

          <form onSubmit={checkAnnuaire} style={{ display: 'flex', gap: 10, maxWidth: 480, margin: '0 auto 24px' }}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={9}
              placeholder="Entrez votre SIREN (9 chiffres)"
              value={annuaireSiren}
              onChange={e => {
                setAnnuaireSiren(e.target.value.replace(/\D/g, '').slice(0, 9));
                setAnnuaireResult(null);
                setAnnuaireError('');
              }}
              style={{
                flex: 1,
                padding: '11px 16px',
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                fontSize: 15,
                fontFamily: 'var(--font-sans)',
                outline: 'none',
                letterSpacing: '0.05em',
              }}
            />
            <button
              type="submit"
              disabled={annuaireSiren.length !== 9 || annuaireLoading}
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 20px', whiteSpace: 'nowrap' }}
            >
              {annuaireLoading ? (
                <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              ) : <Search size={15} />}
              Vérifier
            </button>
          </form>

          {annuaireError && (
            <div style={{ maxWidth: 480, margin: '0 auto 16px', background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 14, color: 'var(--color-danger)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle size={16} /> {annuaireError}
            </div>
          )}

          {annuaireResult && (() => {
            const { entreprise, annuaire } = annuaireResult;

            if (!entreprise) {
              return (
                <div style={{ maxWidth: 580, margin: '0 auto', background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-danger)', fontWeight: 700, marginBottom: 6 }}>
                    <X size={18} /> SIREN introuvable
                  </div>
                  <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0 }}>
                    Ce numéro SIREN n'est pas référencé dans le registre national des entreprises.
                  </p>
                </div>
              );
            }

            const statusBadge = {
              inscrit:     { color: 'var(--color-success)', bg: 'var(--color-success-bg)', border: 'var(--color-success-border)', icon: <CheckCircle2 size={15} />, label: 'Inscrit à l\'annuaire DGFIP' },
              non_inscrit: { color: 'var(--color-warning)', bg: 'var(--color-warning-bg)', border: 'var(--color-warning-border)', icon: <AlertCircle size={15} />, label: 'Non inscrit à l\'annuaire' },
              non_verifie: { color: 'var(--color-primary)', bg: 'var(--color-primary-soft)', border: 'var(--color-info-border)', icon: <Info size={15} />, label: 'Statut non déterminé' },
            }[annuaire.statut] || null;

            const showDgfipCard = annuaire.statut === 'non_verifie' || annuaire.statut === 'non_inscrit';
            const showContactCard = annuaire.statut === 'non_inscrit' || annuaire.statut === 'non_verifie';

            return (
              <div style={{ maxWidth: 580, margin: '0 auto' }}>

                {/* Fiche entreprise */}
                <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '14px 18px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-text)' }}>{entreprise.nom}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                      SIREN {entreprise.siren}{entreprise.ville ? ` — ${entreprise.ville}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 9999, fontSize: 11, fontWeight: 600, background: entreprise.etat === 'A' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)', color: entreprise.etat === 'A' ? 'var(--color-success)' : 'var(--color-danger)' }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                      {entreprise.etat === 'A' ? 'Active' : 'Fermée'}
                    </span>
                    {statusBadge && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 9999, fontSize: 11, fontWeight: 600, background: statusBadge.bg, color: statusBadge.color, border: `1px solid ${statusBadge.border}` }}>
                        {statusBadge.icon} {statusBadge.label}
                      </span>
                    )}
                  </div>
                </div>

                {/* Cas inscrit : message simple */}
                {annuaire.statut === 'inscrit' && (
                  <div style={{ background: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', fontSize: 14, color: 'var(--color-text-secondary)' }}>
                    {annuaire.pdp
                      ? `Votre entreprise est enregistrée avec la plateforme ${annuaire.pdp}.`
                      : 'Votre entreprise est enregistrée dans l\'annuaire de la facturation électronique DGFIP.'}
                  </div>
                )}

                {/* Deux cartes côte à côte : DGFIP / Contact */}
                {(showDgfipCard || showContactCard) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

                    {/* Carte DGFIP */}
                    {showDgfipCard && (
                      <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 'var(--radius-lg)', padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 8, background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Landmark size={17} color="#fff" />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Portail de l'État</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Annuaire DGFIP</div>
                          </div>
                        </div>
                        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.55 }}>
                          Consultez directement l'annuaire officiel de la facturation électronique sur le portail de la DGFIP.
                        </p>
                        <a
                          href="https://facturation.chorus-pro.gouv.fr/annuaire/#/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            marginTop: 'auto',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            padding: '9px 14px', borderRadius: 'var(--radius)',
                            background: '#1e3a5f', color: '#fff',
                            fontSize: 13, fontWeight: 600, textDecoration: 'none',
                          }}
                        >
                          Accéder au portail <ArrowRight size={13} />
                        </a>
                      </div>
                    )}

                    {/* Carte Contact */}
                    {showContactCard && (
                      <div style={{ background: 'var(--color-primary-soft)', border: '1px solid var(--color-info-border)', borderRadius: 'var(--radius-lg)', padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Send size={16} color="#fff" />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>E-facture</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)' }}>Être accompagné</div>
                          </div>
                        </div>
                        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.55 }}>
                          Nos équipes vérifient votre statut et configurent votre inscription via Super PDP, plateforme agréée DGFIP.
                        </p>
                        <button
                          onClick={scrollToContact}
                          className="btn btn-primary"
                          style={{ marginTop: 'auto', fontSize: 13, padding: '9px 14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                        >
                          Prendre contact <ArrowRight size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ padding: '80px 24px', background: 'var(--color-surface)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 12 }}>Tout ce dont vous avez besoin</h2>
            <p style={{ fontSize: 17, color: 'var(--color-text-secondary)', maxWidth: 560, margin: '0 auto' }}>
              Une solution pensée pour les TPE/PME d'Aix-en-Provence, avec toutes les obligations légales intégrées d'emblée.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {[
              { icon: <FileText size={32} />, title: 'Devis & Factures professionnels', desc: 'Créez des devis et factures aux mentions légales obligatoires en quelques secondes. Export PDF prêt à envoyer. Transformez un devis en facture en un clic.' },
              { icon: <ShieldCheck size={32} />, title: 'Conformité 2026 intégrée', desc: 'Numérotation séquentielle, taux TVA, SIRET, conditions de paiement, pénalités de retard — tout est géré. Format Factur-X (EN 16931) généré automatiquement.' },
              { icon: <Send size={32} />, title: 'Transmission Super PDP', desc: 'Envoyez vos factures B2B vers le Portail Public de Facturation via notre intégration Super PDP. Suivez le statut en temps réel : déposée, acceptée, rejetée.' },
            ].map(f => (
              <div key={f.title} style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 32 }}>
                <div style={{ color: 'var(--color-primary)', marginBottom: 16 }}>{f.icon}</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.65 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ARTISANS ── */}
      <section style={{ padding: '80px 24px', background: 'var(--color-bg)', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 12 }}>
              Pensé pour les artisans et petits entrepreneurs
            </h2>
            <p style={{ fontSize: 17, color: 'var(--color-text-secondary)', maxWidth: 640, margin: '0 auto', lineHeight: 1.65 }}>
              Vous connaissez votre métier — pas la réglementation fiscale. Et c'est tout à fait normal.
              E-facture s'occupe de toute la complexité technique à votre place, sans jargon, sans formation.
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, justifyContent: 'center', marginBottom: 56 }}>
            {[
              'Plombier', 'Électricien', 'Maçon', 'Menuisier', 'Peintre en bâtiment',
              'Couvreur', 'Carreleur', 'Chauffagiste', 'Jardinier-paysagiste',
              'Serrurier', 'Charpentier', 'Plaquiste', 'Vitrier', 'Ramoneur',
              'Artisan du bâtiment', 'Auto-entrepreneur', 'Prestataire de services', 'TPE · PME',
            ].map(metier => (
              <span key={metier} style={{
                display: 'inline-flex', alignItems: 'center',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-full)',
                padding: '6px 16px',
                fontSize: 13.5, fontWeight: 500,
                color: 'var(--color-text-secondary)',
              }}>
                {metier}
              </span>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {[
              {
                icon: <PenLine size={26} />,
                title: 'Interface en français, sans jargon',
                desc: 'Créez vos devis et factures en quelques clics. Aucune formation requise — si vous savez utiliser un smartphone, vous savez utiliser E-facture.',
              },
              {
                icon: <ShieldCheck size={26} />,
                title: 'La conformité est automatique',
                desc: 'Factur-X, PDP agréée, mentions légales obligatoires, numérotation séquentielle… Vous n\'avez pas à connaître ces termes. On s\'en charge.',
              },
              {
                icon: <Bell size={26} />,
                title: 'Suivi en temps réel',
                desc: 'Vous savez à tout moment si votre facture a été acceptée. Fini l\'incertitude sur le statut de vos paiements professionnels.',
              },
            ].map((item, i) => (
              <div key={i} style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 32,
              }}>
                <div style={{ color: 'var(--color-primary)', marginBottom: 14 }}>{item.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 10 }}>{item.title}</div>
                <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.65, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button onClick={scrollToContact} className="btn btn-primary" style={{ fontSize: 15, padding: '12px 28px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Vérifier ma situation <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {/* ── CE QUI CHANGE ── */}
      <section ref={reformeRef} id="reforme" style={{ padding: '80px 24px', background: 'var(--color-bg)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 'var(--radius-full)', padding: '4px 14px', fontSize: 12, fontWeight: 600, color: 'var(--color-warning)', marginBottom: 16 }}>
              <Clock size={13} /> {jours} jours restants
            </div>
            <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 12 }}>Ce qui change au 1er septembre 2026</h2>
            <p style={{ fontSize: 17, color: 'var(--color-text-secondary)', maxWidth: 600, margin: '0 auto' }}>
              La réforme de facturation électronique concerne toutes les entreprises assujetties à la TVA en France.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'start' }}>
            {/* Timeline */}
            <div>
              {[
                {
                  date: '1er sept. 2026',
                  color: 'var(--color-danger)',
                  bg: 'var(--color-danger-bg)',
                  icon: <Download size={20} />,
                  title: 'Obligation de recevoir',
                  desc: 'Toutes les entreprises assujetties à la TVA devront être capables de recevoir des factures au format électronique via une PDP ou le PPF.',
                },
                {
                  date: '1er sept. 2026',
                  color: 'var(--color-primary)',
                  bg: 'var(--color-primary-soft)',
                  icon: <Building2 size={20} />,
                  title: 'Grandes entreprises & ETI',
                  desc: 'Obligation d\'émettre leurs factures B2B au format électronique (Factur-X, UBL ou CII) via une plateforme agréée.',
                },
                {
                  date: '2027',
                  color: 'var(--color-warning)',
                  bg: 'var(--color-warning-bg)',
                  icon: <Store size={20} />,
                  title: 'PME & TPE',
                  desc: 'Extension de l\'obligation d\'émission aux petites et moyennes entreprises ainsi qu\'aux micro-entreprises.',
                },
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 20, marginBottom: i < 2 ? 32 : 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: step.bg, border: `2px solid ${step.color}`, color: step.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {step.icon}
                    </div>
                    {i < 2 && <div style={{ width: 2, flex: 1, background: 'var(--color-border)', marginTop: 8, minHeight: 24 }} />}
                  </div>
                  <div style={{ paddingTop: 8 }}>
                    <span style={{ display: 'inline-block', background: step.bg, color: step.color, borderRadius: 'var(--radius-sm)', padding: '2px 10px', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                      {step.date}
                    </span>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{step.title}</div>
                    <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.65, margin: 0 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Ce qui disparaît vs ce qui reste */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-danger)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <X size={15} /> Ce qui disparaît
                </div>
                {['Facture PDF envoyée par email entre assujettis TVA', 'Facture papier envoyée par courrier', 'Portail fournisseur sans format structuré'].map(item => (
                  <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                    <span style={{ color: 'var(--color-danger)', flexShrink: 0, marginTop: 1 }}><X size={14} /></span>
                    {item}
                  </div>
                ))}
              </div>
              <div style={{ background: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-success)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Check size={15} /> Ce qui reste valable
                </div>
                {['Facturation B2C (particuliers) : pas concernée', 'Auto-entrepreneurs : régime simplifié possible', 'Factures d\'acompte et avoirs : même format'].map(item => (
                  <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
                    <span style={{ color: 'var(--color-success)', flexShrink: 0, marginTop: 1 }}><Check size={14} /></span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LA SOLUTION ── */}
      <section ref={solutionRef} id="solution" style={{ padding: '80px 24px', background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 12 }}>Comment ça marche</h2>
            <p style={{ fontSize: 17, color: 'var(--color-text-secondary)', maxWidth: 560, margin: '0 auto' }}>
              E-facture gère tout le cycle de vie de vos factures électroniques, de la création à l'accusé de réception.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, position: 'relative' }}>
            {[
              { n: '1', icon: <PenLine size={24} />, title: 'Créez', desc: 'Devis ou facture en quelques clics. Toutes les mentions légales sont préremplies.' },
              { n: '2', icon: <FileText size={24} />, title: 'Générez', desc: 'E-facture produit automatiquement le fichier Factur-X (PDF + XML EN 16931).' },
              { n: '3', icon: <Send size={24} />, title: 'Transmettez', desc: 'La facture est envoyée à Super PDP, plateforme agréée DGFIP, qui la dépose sur le PPF.' },
              { n: '4', icon: <Bell size={24} />, title: 'Suivez', desc: 'Statut en temps réel : déposée, acceptée ou rejetée. Vous êtes notifié à chaque étape.' },
            ].map((step, i) => (
              <div key={step.n} style={{ position: 'relative', padding: '0 24px', textAlign: 'center' }}>
                {i < 3 && (
                  <div style={{ position: 'absolute', top: 16, right: -10, zIndex: 1, color: 'var(--color-border)' }}>
                    <ArrowRight size={20} />
                  </div>
                )}
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--color-primary-soft)', border: '2px solid var(--color-primary-light)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  {step.icon}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--color-primary)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{step.n}</span>
                  <span style={{ fontWeight: 700, fontSize: 16 }}>{step.title}</span>
                </div>
                <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.65 }}>{step.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 56, background: 'var(--color-primary-soft)', border: '1px solid var(--color-primary-light)', borderRadius: 'var(--radius-lg)', padding: '28px 36px', display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{ color: 'var(--color-primary)', flexShrink: 0 }}>
              <ShieldCheck size={40} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Super PDP — Plateforme agréée DGFIP</div>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.65 }}>
                E-facture s'appuie sur <strong>Super PDP</strong>, une Plateforme de Dématérialisation Partenaire officiellement agréée par la DGFiP.
                C'est Super PDP qui est l'intermédiaire certifié entre vous et le Portail Public de Facturation (PPF) de l'État.
                Vos données sont hébergées en France et ne transitent que par des infrastructures agréées.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section ref={faqRef} id="faq" style={{ padding: '80px 24px', background: 'var(--color-bg)', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 12 }}>Questions fréquentes</h2>
          </div>
          {[
            {
              q: 'Mon entreprise est-elle concernée par la réforme ?',
              a: 'Oui, si vous êtes assujetti à la TVA et que vous émettez ou recevez des factures B2B en France. Les entreprises exonérées de TVA (auto-entrepreneurs sous le seuil) ont un régime simplifié. La réforme ne concerne pas la facturation aux particuliers (B2C).',
            },
            {
              q: 'Qu\'est-ce qu\'une PDP et pourquoi passer par Super PDP ?',
              a: 'Une PDP (Plateforme de Dématérialisation Partenaire) est un opérateur privé agréé par la DGFiP pour transmettre les factures électroniques au Portail Public de Facturation (PPF). Super PDP est l\'une de ces plateformes agréées. E-facture utilise Super PDP pour vous éviter d\'avoir à vous inscrire directement sur le PPF.',
            },
            {
              q: 'Qu\'est-ce que le format Factur-X ?',
              a: 'Factur-X est le format hybride franco-allemand retenu par la réforme. C\'est un fichier PDF lisible par l\'humain avec un fichier XML structuré (EN 16931) intégré, lisible par les logiciels. E-facture génère ce format automatiquement à chaque facture.',
            },
            {
              q: 'Puis-je continuer à envoyer mes factures par email ?',
              a: 'Entre assujettis TVA, non — le PDF par email n\'est plus accepté après le 1er septembre 2026 pour l\'émission. La facture doit transiter par une PDP agréée. En revanche, vous pouvez toujours envoyer une copie PDF à votre client en plus de la transmission officielle.',
            },
            {
              q: 'Combien de temps faut-il pour se mettre en conformité ?',
              a: 'Avec E-facture, la mise en place prend en général moins d\'une journée : création de votre compte, paramétrage de votre entreprise, et vous pouvez émettre votre première facture électronique conforme. Nous vous accompagnons pas à pas.',
            },
            {
              q: 'Je suis artisan auto-entrepreneur. Suis-je concerné par la réforme ?',
              a: 'Cela dépend de votre situation. Si vous êtes assujetti à la TVA (chiffre d\'affaires au-dessus des seuils de franchise en vigueur), vous devrez émettre et recevoir des factures électroniques. Si vous bénéficiez de la franchise de TVA (art. 293B CGI), vous devrez néanmoins être capable de recevoir des factures électroniques de vos fournisseurs à partir de septembre 2026. En cas de doute, contactez-nous — nous vous aidons à évaluer votre situation gratuitement.',
            },
            {
              q: 'J\'envoie mes factures par email depuis des années. Qu\'est-ce qui change concrètement ?',
              a: 'Entre professionnels assujettis à la TVA, le simple PDF par email ne sera plus suffisant après le 1er septembre 2026. La facture devra passer par une plateforme agréée (PDP). Avec E-facture, vous continuez à créer vos factures exactement comme avant — c\'est nous qui nous chargeons de la transmission officielle. Votre façon de travailler ne change pas.',
            },
            {
              q: 'Est-ce difficile à utiliser pour quelqu\'un peu à l\'aise avec l\'informatique ?',
              a: 'Non. E-facture a été conçu spécifiquement pour les artisans et petits entrepreneurs qui n\'ont pas de formation informatique. L\'interface est entièrement en français, sans aucun jargon technique. Si vous savez envoyer un email ou utiliser votre téléphone, vous saurez utiliser E-facture. Et nous vous accompagnons lors de la prise en main initiale.',
            },
          ].map((item, i) => (
            <div key={i} style={{ borderBottom: '1px solid var(--color-border)', marginBottom: 0 }}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{
                  width: '100%', textAlign: 'left', background: 'none', border: 'none',
                  cursor: 'pointer', padding: '20px 0', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', gap: 16, fontFamily: 'var(--font-sans)',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 16, color: 'var(--color-text)' }}>{item.q}</span>
                <span style={{ fontSize: 20, color: 'var(--color-text-muted)', flexShrink: 0, transform: openFaq === i ? 'rotate(45deg)' : 'none', transition: '150ms' }}>+</span>
              </button>
              {openFaq === i && (
                <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', lineHeight: 1.7, paddingBottom: 20, margin: 0 }}>
                  {item.a}
                </p>
              )}
            </div>
          ))}
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button onClick={scrollToContact} className="btn btn-primary" style={{ fontSize: 15, padding: '12px 28px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Une autre question ? Contactez-nous <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {/* ── SECTION LOCALE ── */}
      <section style={{ background: 'var(--color-primary-soft)', padding: '72px 24px', borderTop: '1px solid var(--color-primary-light)', borderBottom: '1px solid var(--color-primary-light)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-primary)', color: '#fff', borderRadius: 'var(--radius-full)', padding: '4px 14px', fontSize: 12, fontWeight: 600, marginBottom: 16 }}>
              <MapPin size={12} /> Aix-en-Provence & région PACA
            </div>
            <h2 style={{ fontSize: 30, fontWeight: 800, marginBottom: 16, lineHeight: 1.25 }}>
              Un accompagnement local,<br />pas une hotline offshore
            </h2>
            <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', lineHeight: 1.7, marginBottom: 24 }}>
              Facto est développé et supporté depuis Aix-en-Provence. Nous connaissons les réalités des entreprises locales — artisans, prestataires, bureaux d'études, cabinets.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['Configuration initiale offerte pour les premiers clients', 'Support réactif par email et téléphone', 'Accompagnement sur la réforme e-invoicing 2026'].map(item => (
                <li key={item} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15 }}>
                  <span style={{ color: 'var(--color-success)' }}><Check size={16} /></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: 36, boxShadow: 'var(--shadow-md)', border: '1px solid var(--color-primary-light)' }}>
            <div style={{ color: 'var(--color-primary)', marginBottom: 16, textAlign: 'center' }}>
              <Landmark size={42} />
            </div>
            <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 12, textAlign: 'center' }}>La réforme e-invoicing</h3>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.7, marginBottom: 20 }}>
              Depuis le <strong>1er septembre 2026</strong>, toutes les entreprises assujetties à la TVA en France doivent émettre leurs factures B2B au format électronique via une <strong>PDP agréée</strong>.
            </p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13, color: 'var(--color-warning)', fontWeight: 500, width: '100%', boxSizing: 'border-box' }}>
              <Clock size={14} style={{ flexShrink: 0 }} /> Il ne reste que <strong>{jours} jours</strong> pour se mettre en conformité.
            </div>
          </div>
        </div>
      </section>

      {/* ── FORMULAIRE DE CONTACT ── */}
      <section ref={contactRef} id="contact" style={{ padding: '80px 24px', background: 'var(--color-surface)' }}>
        <div style={{ maxWidth: 620, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 30, fontWeight: 800, marginBottom: 10 }}>Prendre contact</h2>
            <p style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>
              Décrivez votre activité — nous vous recontactons sous 24h pour configurer votre accès.
            </p>
          </div>

          {sent ? (
            <div style={{ background: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)', borderRadius: 'var(--radius-lg)', padding: 40, textAlign: 'center' }}>
              <div style={{ color: 'var(--color-success)', marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                <CheckCircle2 size={48} />
              </div>
              <h3 style={{ fontWeight: 700, fontSize: 20, color: 'var(--color-success)', marginBottom: 8 }}>Message envoyé !</h3>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 15 }}>
                Nous vous recontactons sous 24h à l'adresse <strong>{form.email}</strong>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 40, boxShadow: 'var(--shadow-md)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Nom / Prénom *</label>
                  <input required type="text" placeholder="Jean Dupont" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Email *</label>
                  <input required type="email" placeholder="jean@entreprise.fr" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Téléphone</label>
                  <input type="tel" placeholder="06 12 34 56 78" value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>
                    SIREN
                    <span style={{ marginLeft: 6, fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 400 }}>9 chiffres</span>
                  </label>
                  <input
                    type="text" inputMode="numeric" maxLength={9} placeholder="123456789"
                    value={form.siren} onChange={e => handleSirenChange(e.target.value)}
                    style={{ borderColor: sirenStatus === 'found' ? 'var(--color-success)' : sirenStatus === 'notfound' ? 'var(--color-danger)' : undefined }}
                  />
                  {sirenStatus === 'loading' && <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>Vérification…</p>}
                  {sirenStatus === 'found' && sirenInfo && (
                    <p style={{ fontSize: 12, color: 'var(--color-success)', marginTop: 4, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Check size={12} /> {sirenInfo.nom}{sirenInfo.siege?.ville ? ` — ${sirenInfo.siege.ville}` : ''}
                    </p>
                  )}
                  {sirenStatus === 'notfound' && <p style={{ fontSize: 12, color: 'var(--color-danger)', marginTop: 4 }}>SIREN introuvable dans le registre national</p>}
                  {sirenStatus === 'error' && <p style={{ fontSize: 12, color: 'var(--color-warning)', marginTop: 4 }}>Service de vérification indisponible</p>}
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 28 }}>
                <label>Message</label>
                <textarea rows={4} placeholder="Décrivez votre activité, votre volume de factures mensuel, vos questions…" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} style={{ resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
              </div>

              {sendError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{sendError}</div>}

              <button type="submit" className="btn btn-primary" disabled={sending} style={{ width: '100%', fontSize: 16, padding: '14px' }}>
                {sending ? 'Envoi en cours…' : 'Envoyer ma demande'}
              </button>
              <p style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: 'var(--color-text-muted)' }}>Vos données ne sont pas partagées avec des tiers.</p>
            </form>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#0f172a', color: 'rgba(255,255,255,0.6)', padding: '40px 24px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <img src="/e-facture.png" alt="Facto" style={{ height: 28, opacity: 0.8 }} />
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>E-facture</span>
          </div>
          <p style={{ fontSize: 13, marginBottom: 12 }}>Solution de facturation électronique conforme — Aix-en-Provence, France</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 20 }}>
            <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 14, fontFamily: 'var(--font-sans)', textDecoration: 'underline' }}>Se connecter</button>
            <button onClick={scrollToContact} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 14, fontFamily: 'var(--font-sans)', textDecoration: 'underline' }}>Contact</button>
          </div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} E-facture. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
